import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, TrendingUp, Calculator, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalMaterials: 0,
    totalProjects: 0,
    totalCost: 0,
    recentCalculations: 0,
  });

  useEffect(() => {
    // Load stats from localStorage
    const materials = JSON.parse(localStorage.getItem('materials') || '[]');
    const labor = JSON.parse(localStorage.getItem('labor') || '[]');
    const projects = JSON.parse(localStorage.getItem('projects') || '[]');
    
    const totalCost = projects.reduce((sum, project) => sum + (project.totalCost || 0), 0);
    
    setStats({
      totalMaterials: materials.length + labor.length,
      totalProjects: projects.length,
      totalCost: totalCost,
      recentCalculations: projects.filter(p => {
        const projectDate = new Date(p.createdAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return projectDate >= weekAgo;
      }).length,
    });
  }, []);

  const statCards = [
    {
      title: 'Toplam Kalem',
      value: stats.totalMaterials,
      icon: Package,
      description: 'Malzeme ve işçilik sayısı',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: 'Toplam Proje',
      value: stats.totalProjects,
      icon: FolderOpen,
      description: 'Oluşturulan proje sayısı',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      title: 'Toplam Maliyet',
      value: `₺${stats.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: TrendingUp,
      description: 'Tüm projelerin toplamı',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
    },
    {
      title: 'Son Hesaplamalar',
      value: stats.recentCalculations,
      icon: Calculator,
      description: 'Son 7 günde yapılan',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  return (
    <div className="p-8 animate-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Gösterge Paneli</h1>
        <p className="text-muted-foreground text-lg">Maliyet ve işçilik hesaplama sisteminize genel bakış</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="stat-card border-none">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.bgColor} p-2 rounded-lg`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="border-primary/20 hover:border-primary/40 transition-colors">
          <CardHeader>
            <CardTitle className="text-lg">Yeni Hesaplama</CardTitle>
            <CardDescription>Yeni bir proje maliyeti hesaplayın</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/calculator')} 
              className="w-full bg-primary hover:bg-primary/90"
            >
              <Calculator className="mr-2 h-4 w-4" />
              Hesaplama Başlat
            </Button>
          </CardContent>
        </Card>

        <Card className="border-accent/20 hover:border-accent/40 transition-colors">
          <CardHeader>
            <CardTitle className="text-lg">Malzeme Ekle</CardTitle>
            <CardDescription>Yeni malzeme ve fiyat bilgisi ekleyin</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/materials')} 
              className="w-full bg-accent hover:bg-accent/90"
            >
              <Package className="mr-2 h-4 w-4" />
              Malzeme Yönetimi
            </Button>
          </CardContent>
        </Card>

        <Card className="border-secondary/20 hover:border-secondary/40 transition-colors">
          <CardHeader>
            <CardTitle className="text-lg">Projeler</CardTitle>
            <CardDescription>Geçmiş hesaplamalarınızı görüntüleyin</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/projects')} 
              variant="outline"
              className="w-full"
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Projelere Git
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="mt-8 bg-gradient-to-br from-primary/5 to-accent/5 border-none">
        <CardHeader>
          <CardTitle>Hoş Geldiniz!</CardTitle>
          <CardDescription className="text-base">
            MaliyetPro ile malzeme maliyetlerinizi kolayca hesaplayın ve projelerinizi yönetin.
            Malzemelerinizi kategorilere ayırın, birim fiyatlarını belirleyin ve otomatik hesaplama yapın.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default Dashboard;